import React, { memo, useEffect, useState } from 'react'
import { useTranslate, required, FormDataConsumer } from 'react-admin'
import { Grid, Skeleton } from '@mui/material'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { businessRuleServices } from 'src/services/sales/businessRule/businessRule.service'
import { paymentServices } from 'src/services/payment/Payment.service'
import { useFormContext } from 'react-hook-form'

export const GetPaymentChargeComponentFields = memo(({ workUnitToken, record }) => {
  const translate = useTranslate()
  const [paymentTypes, setPaymentTypes] = useState([])
  const [paymentTypesSelected, setPaymentTypesSelected] = useState(record?.payment_type || {})
  const [loadingPaymentTypes, setLoadingPaymentTypes] = useState(false)
  const [bank, setBank] = useState([])
  const [loadingBank, setLoadingBank] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState([])
  const [loadingPaymentMethod, setLoadingPaymentMethod] = useState(false)
  const { resetField, setValue } = useFormContext()

  useEffect(() => {
    if (record?.payment_type) {
      setPaymentTypesSelected(record?.payment_type)
    }
  }, [record?.payment_type])

  useEffect(() => {
    if (paymentTypesSelected.length !== 0 && !isShowBankList(paymentTypesSelected)) {
      resetField('payment_charge.bank', { defaultValue: null })
    }
    if (paymentTypesSelected.length !== 0 && !isShowCardList(paymentTypesSelected)) {
      resetField('payment_charge.card', { defaultValue: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentTypesSelected])

  useEffect(() => {

    if (workUnitToken?.[0]?.hash_work_unit) {
      if (isShowBankList(paymentTypesSelected) || isShowCardList(paymentTypesSelected)) {
        getBank(workUnitToken?.[0]?.hash_work_unit)
      }
      if (isShowCardList(paymentTypesSelected)) {
        getPaymentMethod(workUnitToken?.[0]?.token)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workUnitToken, paymentTypesSelected])

  useEffect(() => {
    if (workUnitToken?.[0]?.token) {
      getPaymentType(workUnitToken)
    }
  }, [workUnitToken])

  const getPaymentType = async (work_units) => {
    try {
      setLoadingPaymentTypes(true)
      const paymentTypesPromises = work_units.map((wu) => {
        let token = wu?.token ? wu.token : wu?.value
        return getPaymentTypesService(token)
      })

      const paymentTypesResults = await Promise.all(paymentTypesPromises)
      const combinedPaymentTypes = paymentTypesResults.flat()
      const uniquePaymentTypes = combinedPaymentTypes.reduce((accumulator, current) => {
        if (!accumulator.some((item) => item.short_name === current.short_name)) {
          accumulator.push(current)
        }
        return accumulator
      }, [])
      setPaymentTypes(uniquePaymentTypes)
      setValue('payment_charge.list_payment_type', uniquePaymentTypes)
    } catch (error) {
    } finally {
      setLoadingPaymentTypes(false)
    }
  }

  const getPaymentTypesService = async (workUnitToken) => {
    try {
      const allPaymentTypes = await businessRuleServices.getPaymentTypesByWorkUnitToken(workUnitToken)
      return allPaymentTypes ? allPaymentTypes.map((elem) => elem.payment_type_relation) : []
    } catch (error) {
      return []
    }
  }

  const getBank = async (work_units) => {
    try {
      setLoadingBank(true)
      const bankPromises = work_units.map((wu) => {
        let hash_work_unit = wu?.hash_work_unit
        if (hash_work_unit) {
          return getBankService(hash_work_unit)
        }
      })
      const bankResults = await Promise.all(bankPromises)
      const combinedBank = bankResults.flat()

      const getWorkUnit = (bank) => {
        return work_units.find(
          (work_unit) => work_unit?.hash_work_unit === bank?.work_unit_relation?.hash_work_unit
        )?.short_name
      }

      const map_back = combinedBank.map((bank) => {
        return {
          code: bank?.code,
          id: bank?.id,
          name: bank?.name,
          work_unit_name: getWorkUnit(bank),
        }
      })
      setBank(map_back)
      setValue('payment_charge.list_bank', map_back)
    } catch (error) {
      setBank([])
    } finally {
      setLoadingBank(false)
    }
  }

  const getBankService = async (workUnitToken) => {
    try {
      const banks = await paymentServices.getBankByWorkUnit(workUnitToken)
      if (banks) {
        return banks
      }
      return []
    } catch (error) {
      return []
    }
  }

  const getPaymentMethod = async (work_units) => {
    try {
      setLoadingPaymentMethod(true)
      const paymentMethodPromises = work_units.map((wu) => {
        let token = wu?.token ? wu.token : wu?.value
        if (token) {
          return getPaymentMethodService(token)
        }
      })
      const paymentMethodResults = await Promise.all(paymentMethodPromises)
      const combinedpaymentMethod = paymentMethodResults.flat()
      const uniquePaymentMethod = combinedpaymentMethod.reduce((accumulator, current) => {
        if (!accumulator.some((item) => item.short_name === current.short_name)) {
          accumulator.push(current)
        }
        return accumulator
      }, [])

      setPaymentMethod(uniquePaymentMethod)
      setValue('payment_charge.list_payment_method', uniquePaymentMethod)
    } catch (error) {
      setBank([])
    } finally {
      setLoadingPaymentMethod(false)
    }
  }

  const getPaymentMethodService = async (workUnitToken) => {
    try {
      const paymentMethod = await paymentServices.getPaymentMethodByWorkUnit(workUnitToken)
      if (paymentMethod) {
        return paymentMethod
      }
      return []
    } catch (error) {
      return []
    }
  }

  const isShowBankList = (list_payment_type) => {
    if (list_payment_type) {
      let hasTransferOrDeposit = list_payment_type.include?.some(
        (str) =>
          str.toUpperCase().includes('TRANSFER') ||
          str.toUpperCase().includes('DEPOSIT') ||
          str.includes('all')
      )
      if (list_payment_type.exclude && Array.isArray(list_payment_type.exclude)) {
        const methods = ['TRANSFER', 'DEPOSIT']
        hasTransferOrDeposit =
          hasTransferOrDeposit ||
          !methods.every((method) =>
            list_payment_type.exclude?.find((str) => str.toUpperCase().includes(method))
          )
      }

      return hasTransferOrDeposit
    }
    return false
  }

  const isShowCardList = (list_payment_type) => {
    if (list_payment_type) {
      let hasCard = list_payment_type.include?.some(
        (str) => str.toUpperCase().includes('CARD') || str.includes('all')
      )
      if (list_payment_type.exclude && Array.isArray(list_payment_type.exclude)) {
        const methods = ['CARD']
        hasCard =
          hasCard ||
          !methods.every((method) =>
            list_payment_type.exclude?.find((str) => str.toUpperCase().includes(method))
          )
      }

      return hasCard
    }
    return false
  }

  const isAlredyExist = (e) => {
    const newValue = e.target.value
    if (Array.isArray(newValue) && newValue?.length === 0) {
      setPaymentTypesSelected({ exclude: null, include: [] })
    } else if (JSON.stringify(paymentTypesSelected) !== JSON.stringify(newValue)) {
      setPaymentTypesSelected(newValue)
    }
  }

  return (
    <Grid container spacing={3}>
      <EditInputsWrap label={translate('ra.field.payment_type')} required>
        {loadingPaymentTypes ? (
          <Skeleton variant='rectangular' width={'100%'} height={40} />
        ) : (
          <SelectInput
            source='payment_charge.payment_type'
            options={paymentTypes}
            label=''
            optionsText={['long_name']}
            optionValue='short_name'
            multiple
            showSearch
            showAllOption
            validate={[required()]}
            onChange={(e) => isAlredyExist(e)}
            defaultValue={record && record?.payment_type}
          />
        )}
      </EditInputsWrap>
      {isShowBankList(paymentTypesSelected) && (
        <EditInputsWrap label={translate('ra.field.bank')} required>
          {loadingBank ? (
            <Skeleton variant='rectangular' width={'100%'} height={40} />
          ) : (
            <SelectInput
              source='payment_charge.bank'
              options={bank}
              label=''
              optionsText={['name', 'work_unit_name']}
              multiple
              showSearch
              showAllOption
              transformDefaultValue
              validate={[required()]}
              defaultValue={record && record?.bank}
            />
          )}
        </EditInputsWrap>
      )}
      {isShowCardList(paymentTypesSelected) && (
        <>
          <EditInputsWrap label={translate('ra.field.payment_method')} required>
            {loadingPaymentMethod ? (
              <Skeleton variant='rectangular' width={'100%'} height={40} />
            ) : (
              <SelectInput
                source='payment_charge.payment_method'
                options={paymentMethod}
                label=''
                optionsText={['long_name']}
                validate={[required()]}
                defaultValue={record && record?.payment_method}
              />
            )}
          </EditInputsWrap>

          <FormDataConsumer>
            {({ formData }) => {
              if (formData?.payment_charge.payment_method) {
                const bankWithOptionCards = paymentMethod.find(
                  (paymentMethod) => paymentMethod.id === formData?.payment_charge.payment_method
                )
                return (
                  <EditInputsWrap label={translate('ra.field.card')} required>
                    {bankWithOptionCards ? (
                      <SelectInput
                        source='payment_charge.card'
                        options={bankWithOptionCards.cards || []}
                        label=''
                        optionsText={['long_name']}
                        validate={[required()]}
                        defaultValue={record && record?.card}
                      />
                    ) : (
                      <Skeleton variant='rectangular' width={'100%'} height={40} />
                    )}
                  </EditInputsWrap>
                )
              }
            }}
          </FormDataConsumer>
        </>
      )}
    </Grid>
  )
})
